// @ts-strict-ignore

import { GetStaticPaths, GetStaticProps } from 'next';

import { PhotoSize, Playlist, SeriesDocument } from 'api';
import { initializeApollo } from 'lib/graphql/client';
import { createLogger } from 'lib/logging';

import { Layout } from 'components/layout';
import SeriesPageHeader from 'components/pages/series/slug/SeriesPageHeader';
import SeriesHead from 'components/pages/series/slug/head';
import SeriesVideos from 'components/pages/series/slug/videos';

const VIDEOS_PER_PAGE = 12;

type SeriesProps = {
  series: {
    name: string;
    slug: string;
    id: string;
    description: string;
    primaryImageSet: PhotoSize[];
  };
  seasons: Playlist[];
};

const Series = ({ series, seasons }: SeriesProps) => {
  const { name, description, slug, primaryImageSet } = series;

  return (
    <Layout>
      <div className="w-full text-black">
        <SeriesHead description={description} title={name} />
        <SeriesPageHeader
          title={name}
          description={description}
          imageSet={primaryImageSet}
          seriesSlug={slug}
          watchNowLink={null}
        />
        <SeriesVideos seriesSlug={slug} seasons={seasons} />
      </div>
    </Layout>
  );
};

export default Series;

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      // Popular topics can be added here to be pre-rendered
    ],
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps = async context => {
  const apolloClient = initializeApollo();
  const { data, error } = await apolloClient.query({
    query: SeriesDocument,
    variables: {
      slug: context.params?.slug,
      after: '0',
      before: String(VIDEOS_PER_PAGE + 1)
    }
  });

  // Logging errors, but data fetching will silently fail and return empty sets
  if (error) {
    const log = createLogger('Series Data');
    log.error('getting series data failed', error);
  }

  if (!data.series) {
    return {
      notFound: true
    };
  }

  const seasons: Playlist[] = [];
  let series = {};

  if (data?.series?.playlists?.nodes) {
    const playlists = data.series.playlists.nodes.filter(item => {
      return item != null;
    });
    const firstPlaylist = playlists[0];
    playlists.forEach(season => {
      seasons.unshift(season); // recent seasons first
    });
    series = {
      name: data.series.name,
      slug: data.series.slug,
      id: data.series.id,
      description: firstPlaylist?.description,
      primaryImageSet: firstPlaylist?.primaryImageSet
    };
  }

  return {
    props: {
      series,
      seasons
    },
    revalidate: 180
  };
};
